import { useState, useRef, useEffect } from "react";

interface Message {
  id: number;
  from: "user" | "agent";
  text: string;
  time: string;
}

const initialMessages: Message[] = [
  {
    id: 1,
    from: "agent",
    text: "¡Hola! Soy el asistente virtual de Vidal Ferrer Asesores. ¿En qué puedo ayudarte hoy?",
    time: "Ahora",
  },
];

const quickReplies = [
  "Quiero una consulta fiscal",
  "Necesito gestión laboral",
  "Información sobre seguros",
  "¿Cuáles son vuestros precios?",
];

const autoResponses: Record<string, string> = {
  fiscal: "Entendido. Para la asesoría fiscal analizamos tu situación y optimizamos tu carga tributaria. ¿Te gustaría concertar una consulta gratuita con uno de nuestros asesores?",
  laboral: "Por supuesto. Gestionamos nóminas, contratos, despidos y relaciones con la Seguridad Social. Te podemos ayudar con cualquier cuestión laboral de tu empresa.",
  seguro: "Ofrecemos seguros de responsabilidad civil, salud, vida, accidentes y mucho más. Analizamos tus necesidades y buscamos la mejor cobertura al mejor precio.",
  precio: "Nuestras tarifas se adaptan a cada cliente. Para autónomos desde 80€/mes y para empresas desde 200€/mes. ¿Quieres que te preparemos un presupuesto personalizado?",
  default: "Gracias por tu mensaje. Un asesor se pondrá en contacto contigo en breve. También puedes llamarnos al +34 96 557 18 31 o escribirnos a assessoriagrevi@gmail.com.",
};

function getAutoResponse(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("fiscal") || lower.includes("impuesto") || lower.includes("irpf") || lower.includes("iva")) return autoResponses.fiscal;
  if (lower.includes("laboral") || lower.includes("nómina") || lower.includes("contrato") || lower.includes("empleado")) return autoResponses.laboral;
  if (lower.includes("seguro")) return autoResponses.seguro;
  if (lower.includes("precio") || lower.includes("coste") || lower.includes("tarifa") || lower.includes("cuánto")) return autoResponses.precio;
  return autoResponses.default;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const now = () =>
    new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { id: Date.now(), from: "user", text: text.trim(), time: now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      const agentMsg: Message = {
        id: Date.now() + 1,
        from: "agent",
        text: getAutoResponse(text),
        time: now(),
      };
      setMessages((prev) => [...prev, agentMsg]);
      setTyping(false);
    }, 1200);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat panel */}
      {open && (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col animate-[fadeInUp_0.25s_ease]">
          {/* Header */}
          <div className="bg-[#6B7F5C] px-4 py-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-customer-service-2-line text-white text-lg" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Vidal Ferrer Asesores</p>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
                <span className="text-white/80 text-xs">En línea · Responde en minutos</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto w-7 h-7 flex items-center justify-center text-white/80 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-64 bg-[#F8F7F4]">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    msg.from === "user"
                      ? "bg-[#6B7F5C] text-white rounded-br-none"
                      : "bg-white text-[#2D2D2D] rounded-bl-none shadow-sm"
                  }`}
                >
                  <p>{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${msg.from === "user" ? "text-white/60 text-right" : "text-gray-400"}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-3 rounded-xl rounded-bl-none shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          <div className="px-3 py-2 bg-white border-t border-gray-100 flex gap-2 overflow-x-auto">
            {quickReplies.map((reply) => (
              <button
                key={reply}
                onClick={() => sendMessage(reply)}
                className="text-xs bg-[#F0F4ED] text-[#6B7F5C] px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-[#6B7F5C] hover:text-white transition-colors cursor-pointer flex-shrink-0"
              >
                {reply}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100 flex gap-2">
            <input
              type="text"
              placeholder="Escribe tu consulta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
              className="flex-1 bg-[#F8F7F4] rounded-lg px-3 py-2 text-sm text-[#2D2D2D] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#6B7F5C]"
            />
            <button
              onClick={() => sendMessage(input)}
              className="w-8 h-8 bg-[#6B7F5C] text-white rounded-lg flex items-center justify-center hover:bg-[#5A6E4B] transition-colors cursor-pointer"
            >
              <i className="ri-send-plane-fill text-sm" />
            </button>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-14 h-14 bg-[#6B7F5C] rounded-full flex items-center justify-center shadow-lg hover:bg-[#5A6E4B] transition-all duration-300 cursor-pointer hover:scale-105"
      >
        {open ? (
          <i className="ri-close-line text-white text-2xl" />
        ) : (
          <i className="ri-message-3-line text-white text-2xl" />
        )}
      </button>
    </div>
  );
}
